/**
 * Singleton client-side store for projection record statuses.
 *
 * Handles:
 * - Batch status fetch for the current results page
 * - Cancellation of stale requests on page change
 * - Polling while any record is queued/processing/retrying/delayed
 * - Manual rebuild + per-item polling until current or failed
 * - ES document refresh after rebuild completes
 */
import { batchStatus, rebuild } from "./projectionService";
import {
  AppbaseAppUrl,
  ReactiveAppAPIKEY,
} from "./constants";

// ─── State ────────────────────────────────────────────────────────────────────

const statusMap = new Map(); // key → { state, ...statusFields, _refreshedData? }
const subscribers = new Set();

// Current-page tracking
let pageAbortController = null;
let currentPageKey = "";
let currentPageIdentities = []; // includes _esId / _esIndex for ES reload

// Polling
let pollingTimer = null;

const ACTIVE_STATES = new Set(["queued", "processing", "retrying", "delayed"]);

// ─── Key builders ─────────────────────────────────────────────────────────────

export function makeKey(identity) {
  if (!identity) return "";
  // Match style records whether or not recordType is echoed back by the Worker
  if (
    identity.recordType === "style" ||
    identity.projection === "jewelry-style"
  ) {
    return `style:jewelry-style:${identity.inventoryDbId}`;
  }
  // finalId is inventoryId+serial — omit it so key survives round-trips
  // where the Worker response may not echo finalId back
  return `serial:${identity.projection}:${identity.inventoryId || ""}:${identity.serial}`;
}

// ─── Subscriber pattern ───────────────────────────────────────────────────────

function notify() {
  subscribers.forEach((fn) => fn());
}

/** Subscribe to any store change. Returns an unsubscribe function. */
export function subscribe(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

export function getStatus(key) {
  return statusMap.get(key) || null;
}

/** Returns refreshed ES source data if available after a rebuild. */
export function getRefreshedData(key) {
  const s = statusMap.get(key);
  return (s && s._refreshedData) || null;
}

function setStatus(key, updates) {
  const prev = statusMap.get(key) || {};
  statusMap.set(key, { ...prev, ...updates });
  notify();
}

// ─── Page-level batch status ──────────────────────────────────────────────────

/**
 * Call this whenever the result page changes.
 * Cancels any stale in-flight request, fetches new batch.
 * @param {Array} identities - full identity objects including _esId / _esIndex
 */
export function loadPageStatuses(identities) {
  // Filter out incomplete identities
  const valid = identities.filter((id) => {
    if (!id || !id.recordType) return false;
    if (id.recordType === "style") return !!id.inventoryDbId;
    return !!(id.serial && id.finalId);
  });

  // Deduplicate by key
  const seen = new Set();
  const unique = valid.filter((id) => {
    const k = makeKey(id);
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  if (!unique.length) return;

  const newPageKey = unique
    .map(makeKey)
    .sort()
    .join("|");

  if (newPageKey === currentPageKey) return;

  // Cancel stale
  if (pageAbortController) pageAbortController.abort();
  clearPollTimer();

  currentPageKey = newPageKey;
  currentPageIdentities = unique;
  pageAbortController = new AbortController();

  // Mark new items as loading (don't overwrite items that are already active)
  unique.forEach((id) => {
    const k = makeKey(id);
    const existing = statusMap.get(k);
    if (!existing || !ACTIVE_STATES.has(existing.state)) {
      setStatus(k, { state: "__loading__" });
    }
  });

  fetchPageStatuses(unique, pageAbortController.signal);
}

async function fetchPageStatuses(identities, signal) {
  try {
    // Strip internal fields before sending to API
    const apiRecords = identities.map(toApiRecord);
    const response = await batchStatus(apiRecords, signal);
    applyStatusResponse(response, identities);
    schedulePollIfNeeded();
  } catch (err) {
    if (err.name === "AbortError") return;
    identities.forEach((id) => setStatus(makeKey(id), { state: "__error__" }));
  }
}

function applyStatusResponse(response, identityList) {
  if (!response || !Array.isArray(response.statuses)) return;
  response.statuses.forEach((entry) => {
    if (!entry) return;
    // Worker returns flat objects: identity fields + status fields combined
    // (no nested entry.identity / entry.status sub-objects)
    const k = makeKey(entry);
    if (!k) return;
    const prev = statusMap.get(k) || {};
    setStatus(k, { ...entry, _refreshedData: prev._refreshedData });
  });
}

// ─── Polling ──────────────────────────────────────────────────────────────────

function schedulePollIfNeeded() {
  const hasActive = currentPageIdentities.some((id) => {
    const s = statusMap.get(makeKey(id));
    return s && ACTIVE_STATES.has(s.state);
  });
  if (hasActive && !pollingTimer) {
    pollingTimer = setTimeout(doPoll, 1500);
  }
}

function clearPollTimer() {
  if (pollingTimer) {
    clearTimeout(pollingTimer);
    pollingTimer = null;
  }
}

async function doPoll() {
  pollingTimer = null;

  const active = currentPageIdentities.filter((id) => {
    const s = statusMap.get(makeKey(id));
    return s && ACTIVE_STATES.has(s.state);
  });

  if (!active.length) return;

  // Snapshot states before update so we can detect transitions
  const prevStates = {};
  active.forEach((id) => {
    prevStates[makeKey(id)] = (statusMap.get(makeKey(id)) || {}).state;
  });

  try {
    const apiRecords = active.map(toApiRecord);
    const response = await batchStatus(apiRecords);
    applyStatusResponse(response, active);

    // Detect newly-current items and reload their ES document
    active.forEach((id) => {
      const k = makeKey(id);
      const newState = (statusMap.get(k) || {}).state;
      if (prevStates[k] !== "current" && newState === "current") {
        if (id._esId && id._esIndex) {
          reloadEsDocument(k, id._esIndex, id._esId);
        }
      }
    });

    schedulePollIfNeeded();
  } catch (err) {
    // Keep polling on transient errors
    schedulePollIfNeeded();
  }
}

// ─── ES document reload ───────────────────────────────────────────────────────

async function reloadEsDocument(key, esIndex, esId) {
  try {
    const url = `${AppbaseAppUrl}${esIndex}/_doc/${encodeURIComponent(esId)}`;
    const res = await fetch(url, {
      headers: { Authorization: `ApiKey ${ReactiveAppAPIKEY}` },
    });
    if (!res.ok) return;
    const data = await res.json();
    if (data && data._source) {
      setStatus(key, { _refreshedData: { _id: esId, ...data._source } });
    }
  } catch (e) {
    // Non-fatal — the icon will still show current state
  }
}

// ─── Manual rebuild ───────────────────────────────────────────────────────────

/**
 * Request a manual rebuild for one record.
 * Immediately transitions the row to queued/loading, then polls until done.
 * @param {Object} identity - full identity including _esId / _esIndex
 */
export async function requestRebuild(identity) {
  const k = makeKey(identity);

  const requestId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

  const record = {
    ...toApiRecord(identity),
    requestId,
    reason:
      identity.recordType === "style"
        ? "User selected Refresh style"
        : "User selected Refresh item",
  };

  // Optimistic UI
  setStatus(k, { state: "queued", _requestId: requestId, _refreshedData: null });

  await doRebuild(record, identity, k);
}

async function doRebuild(record, identity, k) {
  try {
    const response = await rebuild(record);
    // Worker returns flat object (same shape as statuses entries)
    if (response && response.ok !== false) {
      const prev = statusMap.get(k) || {};
      setStatus(k, { ...response, _requestId: prev._requestId, _refreshedData: prev._refreshedData });
    }
    // Ensure identity is in current page for polling
    if (!currentPageIdentities.find((id) => makeKey(id) === k)) {
      currentPageIdentities.push(identity);
    }
    schedulePollIfNeeded();
  } catch (err) {
    const status = err.status;
    if (status === 429) {
      setStatus(k, {
        state: "failed",
        failureCategory: "rate_limited",
        attentionRequired: true,
      });
    } else if (status === 503) {
      const body = err.body || {};
      if (body.accepted && body.retryable) {
        // Retry same requestId after short delay
        setTimeout(() => doRebuild(record, identity, k), 2000);
      } else {
        setStatus(k, { state: "__error__" });
      }
    } else if (status === 409) {
      // requestId conflict — application bug, surface as error
      setStatus(k, { state: "__error__" });
      console.error("[ProjectionStore] 409: requestId conflict", record.requestId);
    } else {
      setStatus(k, { state: "__error__" });
    }
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Strip internal (_esId, _esIndex, _requestId) fields before sending to API */
function toApiRecord(identity) {
  const { _esId, _esIndex, _requestId, ...apiRecord } = identity;
  return apiRecord;
}
